import combatContent from '../../../data/thai-combat-content.json';
import economyContent from '../../../data/thai-hunt-economy.json';
import femaleGhosts from '../../../data/female-ghosts.json';
import equipmentContent from '../../../data/character-equipment.json';
import type { WeaponSchool } from './thaiCombat';

export type GhostContentId = keyof typeof combatContent.ghosts;
export type EconomyItemType = 'ghost-part' | 'weapon' | 'armor' | 'consumable';

export type ItemStack = {
  itemId: string;
  nameTh: string;
  type: EconomyItemType;
  quantity: number;
};

export type ItemInventoryState = {
  stacks: Record<string, number>;
  equipment: Record<string, { level: number; blessing: number }>;
  pityMisses: Record<string, number>;
};

export type WalletState = {
  bie: number;
  spiritEssence: number;
};

export type HuntReward = {
  ghostId: GhostContentId;
  bie: number;
  items: ItemStack[];
  nextPityMisses: number;
};

export type CraftResult = {
  ok: boolean;
  reason?: 'recipe-not-found' | 'insufficient-bie' | 'insufficient-spirit' | 'missing-material';
  inventory: ItemInventoryState;
  wallet: WalletState;
  output?: ItemStack;
};

export type UpgradeResult = {
  ok: boolean;
  success: boolean;
  reason?: 'max-level' | 'item-not-owned' | 'insufficient-bie' | 'missing-material';
  inventory: ItemInventoryState;
  wallet: WalletState;
  level: number;
  blessing: number;
  successChance: number;
};

export type HuntReadiness = {
  targetGhostId: string;
  targetNameTh: string;
  ready: boolean;
  schoolBlocked: boolean;
  schoolRecommended: boolean;
  missing: Array<{ itemId: string; quantity: number; owned: number }>;
  sourceHints: Array<{ itemId: string; ghostId: string }>;
};

type Recipe = (typeof economyContent.recipes)[number];
type DropRule = (typeof combatContent.ghosts)[GhostContentId]['drops'][number];

export const THAI_HUNT_ECONOMY = economyContent;

export function createItemInventory(seed?: Partial<ItemInventoryState>): ItemInventoryState {
  return {
    stacks: { ...(seed?.stacks ?? {}) },
    equipment: Object.fromEntries(
      Object.entries(seed?.equipment ?? {}).map(([id, value]) => [id, { ...value }]),
    ),
    pityMisses: { ...(seed?.pityMisses ?? {}) },
  };
}

export function createWallet(seed?: Partial<WalletState>): WalletState {
  return {
    bie: Math.max(0, Math.trunc(seed?.bie ?? 0)),
    spiritEssence: Math.max(0, Math.trunc(seed?.spiritEssence ?? 0)),
  };
}

function cloneInventory(state: ItemInventoryState): ItemInventoryState {
  return createItemInventory(state);
}

function cloneWallet(state: WalletState): WalletState {
  return createWallet(state);
}

function safeRoll(rng: () => number): number {
  return Math.max(0, Math.min(0.999999999, rng()));
}

export function itemName(itemId: string): string {
  const equipment=equipmentContent.find(item=>item.id===itemId);if(equipment)return equipment.name;
  if(itemId==='starter-pet-box')return 'กล่องเพื่อนคู่ใจเริ่มต้น';
  if(itemId==='nine-lives')return 'เก้าชีวิต';
  if(itemId.startsWith('bound-ghost-'))return itemName('ghost-specimen-'+itemId.slice(12)).replace('ผีในยันต์ · ','บริวาร · ');
  if(itemId==='capture-talisman')return 'ยันต์จับผี';
  if(itemId==='warp-talisman')return 'ยันต์ข้ามแดน';
  if(itemId==='potion')return 'น้ำยาฟื้นกาย';
  if(itemId.startsWith('ghost-specimen-')){
    const id=itemId.slice(15),normal:Record<string,string>={pop:'ปอบ',krasue:'กระสือ',tanee:'ตานี',krahang:'กระหัง','violent-dead':'ผีตายโหง','nang-kwak':'นางกวัก','kuman-thong':'กุมารทอง'};
    return 'ผีในยันต์ · '+(femaleGhosts.find(g=>g.id===id)?.nameTh??normal[id]??id);
  }
  for (const ghost of Object.values(combatContent.ghosts)) {
    const rule = ghost.drops.find((drop) => drop.itemId === itemId);
    if (rule) return rule.nameTh;
  }
  const recipe = economyContent.recipes.find((entry) => entry.output.itemId === itemId);
  return recipe?.nameTh ?? itemId;
}

function itemType(itemId: string): EconomyItemType {
  for (const ghost of Object.values(combatContent.ghosts)) {
    const rule = ghost.drops.find((drop) => drop.itemId === itemId);
    if (rule) return rule.type as EconomyItemType;
  }
  const recipe = economyContent.recipes.find((entry) => entry.output.itemId === itemId);
  return (recipe?.output.type ?? 'ghost-part') as EconomyItemType;
}

function addStack(state: ItemInventoryState, item: ItemStack): void {
  state.stacks[item.itemId] = Math.max(0, Math.trunc(state.stacks[item.itemId] ?? 0))
    + Math.max(1, Math.trunc(item.quantity));
  if ((item.type === 'weapon' || item.type === 'armor') && !state.equipment[item.itemId]) {
    state.equipment[item.itemId] = { level: 0, blessing: 0 };
  }
}

function dropRuleFor(itemId: string): DropRule | undefined {
  for (const ghost of Object.values(combatContent.ghosts)) {
    const rule = ghost.drops.find((drop) => drop.itemId === itemId);
    if (rule) return rule as DropRule;
  }
  return undefined;
}

export function rollHuntReward(
  ghostId: GhostContentId,
  pityMisses: number,
  rng: () => number = Math.random,
): HuntReward {
  const ghost = combatContent.ghosts[ghostId];
  const currency = (economyContent.currencyByGhost as Record<string, { bieMin: number; bieMax: number }>)[ghostId];
  const min = Math.max(0, Math.trunc(currency?.bieMin ?? 0));
  const max = Math.max(min, Math.trunc(currency?.bieMax ?? min));
  const bie = min + Math.floor(safeRoll(rng) * (max - min + 1));

  const items: ItemStack[] = [];
  for (const rule of ghost.drops) {
    if (safeRoll(rng) >= rule.chance) continue;
    items.push({
      itemId: rule.itemId,
      nameTh: rule.nameTh,
      type: rule.type as EconomyItemType,
      quantity: Math.max(1, Math.trunc(rule.quantity)),
    });
  }

  const pity = (economyContent.signaturePity as Record<string, { itemId: string; guaranteeAfter: number }>)[ghostId];
  let nextPityMisses = Math.max(0, Math.trunc(pityMisses));
  if (pity) {
    const gotSignature = items.some((item) => item.itemId === pity.itemId);
    if (gotSignature) {
      nextPityMisses = 0;
    } else {
      nextPityMisses += 1;
      if (nextPityMisses >= pity.guaranteeAfter) {
        const rule = dropRuleFor(pity.itemId);
        items.push({
          itemId: pity.itemId,
          nameTh: rule?.nameTh ?? itemName(pity.itemId),
          type: (rule?.type ?? itemType(pity.itemId)) as EconomyItemType,
          quantity: Math.max(1, Math.trunc(rule?.quantity ?? 1)),
        });
        nextPityMisses = 0;
      }
    }
  }

  return { ghostId, bie, items, nextPityMisses };
}

export function applyHuntItems(
  inventory: ItemInventoryState,
  reward: HuntReward,
): ItemInventoryState {
  const next = cloneInventory(inventory);
  for (const item of reward.items) addStack(next, item);
  next.pityMisses[reward.ghostId] = reward.nextPityMisses;
  return next;
}

export function recipeById(recipeId: string): Recipe | undefined {
  return economyContent.recipes.find((recipe) => recipe.id === recipeId);
}

export function craftRecipe(
  inventory: ItemInventoryState,
  wallet: WalletState,
  recipeId: string,
): CraftResult {
  const recipe = recipeById(recipeId);
  if (!recipe) {
    return { ok: false, reason: 'recipe-not-found', inventory: cloneInventory(inventory), wallet: cloneWallet(wallet) };
  }
  if (wallet.bie < recipe.bie) {
    return { ok: false, reason: 'insufficient-bie', inventory: cloneInventory(inventory), wallet: cloneWallet(wallet) };
  }
  if (wallet.spiritEssence < recipe.spiritEssence) {
    return { ok: false, reason: 'insufficient-spirit', inventory: cloneInventory(inventory), wallet: cloneWallet(wallet) };
  }
  for (const ingredient of recipe.ingredients) {
    if ((inventory.stacks[ingredient.itemId] ?? 0) < ingredient.quantity) {
      return { ok: false, reason: 'missing-material', inventory: cloneInventory(inventory), wallet: cloneWallet(wallet) };
    }
  }

  const nextInventory = cloneInventory(inventory);
  const nextWallet = cloneWallet(wallet);
  nextWallet.bie -= recipe.bie;
  nextWallet.spiritEssence -= recipe.spiritEssence;
  for (const ingredient of recipe.ingredients) {
    nextInventory.stacks[ingredient.itemId] =
      Math.max(0, (nextInventory.stacks[ingredient.itemId] ?? 0) - ingredient.quantity);
  }
  const output: ItemStack = {
    itemId: recipe.output.itemId,
    nameTh: recipe.nameTh,
    type: recipe.output.type as EconomyItemType,
    quantity: recipe.output.quantity,
  };
  addStack(nextInventory, output);
  return { ok: true, inventory: nextInventory, wallet: nextWallet, output };
}

function materialForUpgrade(itemId: string, targetLevel: number): { itemId: string; quantity: number } | undefined {
  const profiles = economyContent.upgrade.profiles as Record<string, Array<{ minTargetLevel: number; itemId: string; quantity: number }>>;
  const rules = profiles[itemId] ?? [];
  return [...rules]
    .filter((rule) => targetLevel >= rule.minTargetLevel)
    .sort((a, b) => b.minTargetLevel - a.minTargetLevel)[0];
}

export function upgradeEquipment(
  inventory: ItemInventoryState,
  wallet: WalletState,
  itemId: string,
  rng: () => number = Math.random,
): UpgradeResult {
  const current = inventory.equipment[itemId];
  if (!current || (inventory.stacks[itemId] ?? 0) <= 0) {
    return {
      ok: false,
      success: false,
      reason: 'item-not-owned',
      inventory: cloneInventory(inventory),
      wallet: cloneWallet(wallet),
      level: current?.level ?? 0,
      blessing: current?.blessing ?? 0,
      successChance: 0,
    };
  }

  const maxLevel = economyContent.upgrade.maxLevel;
  if (current.level >= maxLevel) {
    return {
      ok: false,
      success: false,
      reason: 'max-level',
      inventory: cloneInventory(inventory),
      wallet: cloneWallet(wallet),
      level: current.level,
      blessing: current.blessing,
      successChance: 0,
    };
  }

  const targetLevel = current.level + 1;
  const successChance = (economyContent.upgrade.successByTargetLevel as Record<string, number>)[String(targetLevel)] ?? 0;
  const bieCost = (economyContent.upgrade.bieByTargetLevel as Record<string, number>)[String(targetLevel)] ?? 0;
  if (wallet.bie < bieCost) {
    return {
      ok: false,
      success: false,
      reason: 'insufficient-bie',
      inventory: cloneInventory(inventory),
      wallet: cloneWallet(wallet),
      level: current.level,
      blessing: current.blessing,
      successChance,
    };
  }

  const material = materialForUpgrade(itemId, targetLevel);
  if (material && (inventory.stacks[material.itemId] ?? 0) < material.quantity) {
    return {
      ok: false,
      success: false,
      reason: 'missing-material',
      inventory: cloneInventory(inventory),
      wallet: cloneWallet(wallet),
      level: current.level,
      blessing: current.blessing,
      successChance,
    };
  }

  const nextInventory = cloneInventory(inventory);
  const nextWallet = cloneWallet(wallet);
  nextWallet.bie -= bieCost;
  if (material) {
    nextInventory.stacks[material.itemId] =
      Math.max(0, (nextInventory.stacks[material.itemId] ?? 0) - material.quantity);
  }

  const guaranteed = current.blessing >= 100;
  const success = guaranteed || safeRoll(rng) < successChance;
  const nextEntry = nextInventory.equipment[itemId] ?? { level: current.level, blessing: current.blessing };
  if (success) {
    nextEntry.level = targetLevel;
    nextEntry.blessing = 0;
  } else {
    nextEntry.blessing = Math.min(100, current.blessing + economyContent.upgrade.blessingPerFailure);
  }
  nextInventory.equipment[itemId] = nextEntry;

  return {
    ok: true,
    success,
    inventory: nextInventory,
    wallet: nextWallet,
    level: nextEntry.level,
    blessing: nextEntry.blessing,
    successChance,
  };
}

export function huntReadiness(
  targetGhostId: string,
  school: WeaponSchool,
  inventory: ItemInventoryState,
): HuntReadiness | null {
  const plan = (economyContent.huntPlans as Record<string, {
    nameTh: string;
    recommendedSchools: string[];
    blockedSchools: string[];
    requiredItems: Array<{ itemId: string; quantity: number }>;
    sourceHints: Array<{ itemId: string; ghostId: string }>;
  }>)[targetGhostId];
  if (!plan) return null;

  const missing = plan.requiredItems
    .map((required) => ({
      ...required,
      owned: Math.max(0, Math.trunc(inventory.stacks[required.itemId] ?? 0)),
    }))
    .filter((required) => required.owned < required.quantity);

  const schoolBlocked = plan.blockedSchools.includes(school);
  return {
    targetGhostId,
    targetNameTh: plan.nameTh,
    ready: !schoolBlocked && missing.length === 0,
    schoolBlocked,
    schoolRecommended: plan.recommendedSchools.includes(school),
    missing,
    sourceHints: plan.sourceHints.map((hint) => ({ ...hint })),
  };
}

export function rewardSummary(reward: HuntReward): string {
  const itemText = reward.items.map((item) => `${item.nameTh} ×${item.quantity}`).join(' • ');
  return [`+${reward.bie} เบี้ย`, itemText].filter(Boolean).join(' • ');
}
